import * as z from 'zod'

type ItemMap = Map<string, { scopeIndex: number; itemIndex: number }>

function findKeyByValue<T extends object>(obj: T, value: unknown): string {
  const rec = obj as Record<string, unknown>
  const k = Object.keys(rec).find(key => rec[key] === value)
  return k ?? 'items'
}

/**
 * Creates a Zod refinement that ensures unique IDs across multiple scopes.
 *
 * @param config - The configuration for the uniqueness check.
 * @param config.getScope - A function to extract the list of scopes from the data.
 *   Uniqueness is enforced across all items found in all these scopes.
 * @param config.getUniqItemsInScope - A function to extract the list of items from a given scope.
 *   The `id` property of these items is what must be unique across all scopes.
 * @param config.errorMessage - Optional custom error message generator.
 */
const createUniqueIdRefine = <
  Data extends object,
  Scope extends object,
  Children extends { id: string },
>(config: {
  getScope: (data: Data) => Scope[]
  getUniqItemsInScope: (scope: Scope) => Children[]
  errorMessage?: (key: string, id: string) => string
}) => {
  return (data: Data, ctx: z.RefinementCtx) => {
    const itemsMap: ItemMap = new Map()
    const scopes = config.getScope(data)
    const scopeKey = findKeyByValue(data, scopes)
    scopes.forEach((scope, scopeIndex) => {
      const items = config.getUniqItemsInScope(scope)
      const itemKey = findKeyByValue(scope, items)

      items.forEach(({ id }, itemIndex) => {
        const existing = itemsMap.get(id)

        if (existing) {
          const fallbackMessage = `${itemKey}.id "${id}" is duplicated`
          const errorMessage =
            config.errorMessage?.(itemKey, id) ?? fallbackMessage
          const message = `${errorMessage}. First occurrence in ${scopeKey}[${existing.scopeIndex}].${itemKey}[${existing.itemIndex}], duplicate in ${scopeKey}[${scopeIndex}].${itemKey}[${itemIndex}]`
          const path = [scopeKey, scopeIndex, itemKey, itemIndex, 'id']
          ctx.addIssue({ code: 'custom', path, message })
        } else {
          itemsMap.set(id, { scopeIndex, itemIndex })
        }
      })
    })
  }
}

export default createUniqueIdRefine
